'use client';

import { KeyRound, X } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';

import {
  listPasskeysAction,
  passkeyRegisterOptionsAction,
  passkeyRegisterVerifyAction,
} from '@/app/[locale]/(legacy)/mon-espace/compte/securite/_components/passkeys/action';
import PasskeyEnrollForm, {
  guessDeviceName,
} from '@/app/[locale]/(legacy)/mon-espace/compte/securite/_components/passkeys/passkey-enroll-form';
import { canEnrollPasskey, createPasskey } from '@/lib/passkeys/client';
import {
  consumePasskeyOffer,
  isPasskeyOfferSkipped,
  skipPasskeyOffer,
} from '@/lib/passkeys/offer';

/**
 * Mise à niveau silencieuse (connexion par mot de passe) : le gestionnaire de mots de passe
 * crée la clé sans fenêtre s'il vient de fournir le mot de passe Sowefund ; toute erreur
 * (navigateur ancien, mot de passe tapé à la main, refus) vaut simplement « pas de clé ».
 */
async function trySilentUpgrade(defaultDeviceName: string): Promise<boolean> {
  try {
    const options = await passkeyRegisterOptionsAction();
    if (options.status === 'error') {
      return false;
    }
    const credential = await createPasskey(options.data, true);
    const result = await passkeyRegisterVerifyAction(
      guessDeviceName() || defaultDeviceName,
      credential,
    );
    return result.status === 'success';
  } catch {
    return false;
  }
}

/**
 * Encart non bloquant, en bas de l'écran, proposé une fois après une connexion si l'appareil
 * sait créer une clé d'accès et que le membre n'en a encore aucune.
 * Monté dans les layouts (legacy) et (new) ; ne rend rien tant que le drapeau de session
 * n'est pas posé.
 */
export default function PasskeyEnrollOffer() {
  const t = useTranslations('auth.passkey');
  const [state, setState] = useState<'hidden' | 'offer' | 'done'>('hidden');

  useEffect(() => {
    const mode = consumePasskeyOffer();
    if (!mode || isPasskeyOfferSkipped()) {
      return;
    }
    (async () => {
      if (!(await canEnrollPasskey())) {
        return;
      }
      const list = await listPasskeysAction();
      if (list.status !== 'success' || list.data.length > 0) {
        return;
      }
      // Mot de passe : la mise à niveau silencieuse d'abord ; si elle a eu lieu, rien à montrer.
      if (
        mode === 'upgrade' &&
        (await trySilentUpgrade(t('defaultDeviceName')))
      ) {
        return;
      }
      setState('offer');
    })();
  }, [t]);

  if (state === 'hidden') {
    return null;
  }

  const close = () => setState('hidden');

  return (
    <div
      role="dialog"
      aria-label={t('offerTitle')}
      data-testid="passkey-enroll-offer"
      className="fixed inset-x-4 bottom-4 z-50 mx-auto max-w-md rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4 shadow-xl sm:inset-x-auto sm:right-6"
    >
      <div className="flex items-start gap-3">
        <KeyRound className="mt-0.5 h-5 w-5 shrink-0" strokeWidth={1.75} />
        <div className="min-w-0 flex-1">
          {state === 'done' ? (
            <p className="text-[14px] font-medium">{t('offerDone')}</p>
          ) : (
            <>
              <p className="text-[15px] font-semibold">{t('offerTitle')}</p>
              <p className="mt-1 text-[13px] text-[hsl(var(--muted-foreground))]">
                {t('offerDescription')}
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <PasskeyEnrollForm compact onCreated={() => setState('done')} />
                <button
                  type="button"
                  onClick={() => {
                    skipPasskeyOffer();
                    close();
                  }}
                  className="text-sm font-semibold text-primary hover:underline"
                  data-testid="passkey-enroll-offer-later"
                >
                  {t('later')}
                </button>
              </div>
            </>
          )}
        </div>
        <button
          type="button"
          onClick={close}
          aria-label={t('close')}
          className="shrink-0 rounded-md p-1 text-[hsl(var(--muted-foreground))] hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
